// Apex Business Intelligence — Parked Pre-Round Features (additive)
//   1. MyProfilePage          — Change password (all users)
//   2. PayrollProvidersPage   — Gusto / ADP "Coming Soon" placeholder (owner-only)
//   3. SalesQuarterlyAndMoM   — Quarterly goals + 12-month MoM widget (owner-only injection into Sales page)
//   4. LinkTeamMemberPicker   — Helper for Team & Payroll Add modal (link to iv_team_members)
import React, { useState, useEffect, useCallback } from "react";

const API = process.env.REACT_APP_BACKEND_URL;
const TOKEN_KEY = "apex_auth_token";
const auth = () => { const t = localStorage.getItem(TOKEN_KEY); return t ? { Authorization: "Bearer " + t } : {}; };
const j = (path, opts = {}) =>
  fetch(`${API}${path}`, { ...opts, headers: { "Content-Type": "application/json", ...auth(), ...(opts.headers || {}) } })
    .then(async r => { const d = await r.json().catch(() => ({})); if (!r.ok) throw Object.assign(new Error(d.detail || r.statusText), { data: d, status: r.status }); return d; });


// ─── 1) My Profile · Change Password ────────────────────────────────────────
export function MyProfilePage({ user }) {
  const [cur, setCur] = useState("");
  const [n1, setN1] = useState("");
  const [n2, setN2] = useState("");
  const [busy, setBusy] = useState(false);
  const [flash, setFlash] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setFlash(null);
    if (n1.length < 6) { setFlash({ kind: "err", msg: "New password must be 6+ characters." }); return; }
    if (n1 !== n2)    { setFlash({ kind: "err", msg: "New passwords do not match." }); return; }
    setBusy(true);
    try {
      await j("/api/auth/change-password", { method: "POST", body: JSON.stringify({ current_password: cur, new_password: n1 }) });
      setFlash({ kind: "ok", msg: "Password updated. Please use your new password next time you sign in." });
      setCur(""); setN1(""); setN2("");
    } catch (e2) {
      setFlash({ kind: "err", msg: e2.message || "Failed to change password" });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="my-profile-page" style={{ maxWidth: 640 }}>
      <div className="card" style={{ marginBottom: 16 }}>
        <div className="card-header">
          <span className="card-title">My Profile</span>
          <span className="badge badge-gray">{user?.role || "member"}</span>
        </div>
        <div style={{ padding: 16, fontSize: 12, color: "var(--text2)" }}>
          <div><strong>Name:</strong> {user?.name || "—"}</div>
          <div><strong>Email:</strong> {user?.email || "—"}</div>
          <div><strong>Role:</strong> {user?.role || "—"}</div>
          {user?.workspace_id && <div><strong>Workspace:</strong> {user.workspace_id}</div>}
        </div>
      </div>

      <div className="card">
        <div className="card-header">
          <span className="card-title">Change Password</span>
        </div>
        <form onSubmit={submit} style={{ padding: 16 }}>
          <div className="form-group">
            <div className="form-lbl">Current Password</div>
            <input data-testid="cp-current" type="password" required autoComplete="current-password"
              value={cur} onChange={e => setCur(e.target.value)} className="form-control" style={{ width: "100%" }} />
          </div>
          <div className="form-group" style={{ marginTop: 12 }}>
            <div className="form-lbl">New Password (6+ characters)</div>
            <input data-testid="cp-new1" type="password" required minLength={6} autoComplete="new-password"
              value={n1} onChange={e => setN1(e.target.value)} className="form-control" style={{ width: "100%" }} />
          </div>
          <div className="form-group" style={{ marginTop: 12 }}>
            <div className="form-lbl">Confirm New Password</div>
            <input data-testid="cp-new2" type="password" required minLength={6} autoComplete="new-password"
              value={n2} onChange={e => setN2(e.target.value)} className="form-control" style={{ width: "100%" }} />
          </div>
          {flash && (
            <div data-testid="cp-flash" style={{ marginTop: 12, padding: 10, borderRadius: 4, fontSize: 12,
              background: flash.kind === "ok" ? "#dcfce7" : "#fee2e2",
              color:      flash.kind === "ok" ? "#166534" : "#991b1b" }}>{flash.msg}</div>
          )}
          <button data-testid="cp-submit" type="submit" disabled={busy}
            className="btn btn-primary" style={{ marginTop: 14, fontSize: 12 }}>
            {busy ? "Updating…" : "Update Password"}
          </button>
        </form>
      </div>
    </div>
  );
}


// ─── 2) Payroll Providers · Gusto / ADP Coming Soon ────────────────────────
function ProviderCard({ name, tagline, eta, dataTestid, logoBg, logoText }) {
  return (
    <div data-testid={dataTestid} className="integration-card" style={{ position: "relative" }}>
      <div style={{ position: "absolute", top: 12, right: 12, padding: "2px 10px", borderRadius: 999,
        fontSize: 10, background: "#f59e0b", color: "#fff", fontWeight: 700, letterSpacing: 1 }}>COMING SOON</div>
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 14 }}>
        <div style={{ width: 48, height: 48, borderRadius: 8, background: logoBg, color: "#fff",
          display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 800, fontSize: 18,
          fontFamily: "var(--rajdhani)" }}>{logoText}</div>
        <div>
          <div style={{ fontFamily: "var(--rajdhani)", fontSize: 18, fontWeight: 700 }}>{name}</div>
          <div style={{ fontSize: 11, color: "var(--text3)" }}>{tagline}</div>
        </div>
      </div>
      <div style={{ fontSize: 12, color: "var(--text2)", marginBottom: 10, lineHeight: 1.5 }}>
        Once integrated, you'll be able to:
        <ul style={{ marginTop: 6, paddingLeft: 18 }}>
          <li>One-click sync employees and pay rates from {name}</li>
          <li>Auto-import payroll runs into Office P&L</li>
          <li>Push approved timecards from Apex → {name} for payment</li>
          <li>Tax & benefits reconciliation on a single dashboard</li>
        </ul>
      </div>
      <div className="stat-row"><span style={{ fontSize: 11, color: "var(--text3)" }}>Target</span><span style={{ fontSize: 11 }}>{eta}</span></div>
      <div className="stat-row"><span style={{ fontSize: 11, color: "var(--text3)" }}>Status</span><span style={{ fontSize: 11, color: "#f59e0b" }}>Roadmap · Not yet available</span></div>
      <button className="btn btn-ghost" disabled style={{ width: "100%", marginTop: 12, fontSize: 11, opacity: 0.6, cursor: "not-allowed" }}
        data-testid={`${dataTestid}-cta`}>Notify me when ready</button>
    </div>
  );
}

export function PayrollProvidersPage() {
  return (
    <div data-testid="payroll-providers-page">
      <div className="card" style={{ marginBottom: 16, padding: "14px 18px" }}>
        <div style={{ fontFamily: "var(--rajdhani)", fontSize: 15, fontWeight: 700 }}>Payroll Providers</div>
        <div style={{ fontSize: 11, color: "var(--text3)" }}>
          Native integrations to push timecards and pull payroll into Team & Payroll + Office P&L. Live integrations launching in upcoming releases.
        </div>
      </div>
      <div className="integration-grid">
        <ProviderCard name="Gusto" tagline="People platform · payroll, benefits, HR" eta="Q2 2026"
          dataTestid="pp-gusto-card" logoBg="#f47629" logoText="G" />
        <ProviderCard name="ADP" tagline="Workforce Now · enterprise payroll & HCM" eta="Q3 2026"
          dataTestid="pp-adp-card" logoBg="#d6232a" logoText="ADP" />
      </div>
      <div className="card" style={{ marginTop: 16, padding: "14px 18px" }}>
        <div style={{ fontFamily: "var(--rajdhani)", fontSize: 13, fontWeight: 700, marginBottom: 6 }}>
          Already using QuickBooks Online?
        </div>
        <div style={{ fontSize: 11, color: "var(--text3)" }}>
          The <strong>QuickBooks Sync</strong> page (Administration menu) is live now — you can connect your QBO company and sync customers, invoices, and payments today.
        </div>
      </div>
    </div>
  );
}


// ─── 3) Sales · Quarterly Goals + MoM widget ───────────────────────────────
export function SalesQuarterlyAndMoM({ locations }) {
  const [mom, setMom] = useState(null);
  const [qData, setQData] = useState(null);
  const [year, setYear] = useState(new Date().getFullYear());
  const [editQ, setEditQ] = useState(null);
  const [target, setTarget] = useState("");
  const [wsSel, setWsSel] = useState((locations && locations[0]?.id) ? String(locations[0].id) : "1");

  const load = useCallback(() => {
    j("/api/fs/sales/mom?months=12").then(setMom).catch(() => {});
    j(`/api/fs/sales/quarterly?year=${year}`).then(setQData).catch(() => {});
  }, [year]);
  useEffect(() => { load(); }, [load]);

  const saveQuarterGoal = async () => {
    const t = parseFloat(target);
    if (isNaN(t) || t < 0) { alert("Enter a valid target ($)"); return; }
    try {
      await j("/api/fs/goals", { method: "PUT", body: JSON.stringify({
        workspace_id: String(wsSel), period: editQ, period_type: "quarter", target: t,
      })});
      setEditQ(null); setTarget(""); load();
    } catch (e) { alert(e.message); }
  };

  const fmt = (n) => `$${(Number(n || 0)).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
  const months = mom?.months || [];
  const maxTotal = Math.max(1, ...months.map(m => m.total));

  return (
    <>
      <div className="card" data-testid="sales-mom-card" style={{ marginTop: 16, marginBottom: 16 }}>
        <div className="card-header">
          <span className="card-title">Sales · Month-over-Month (last 12)</span>
          <span className="badge badge-cyan">{months.length} months</span>
        </div>
        <div style={{ padding: 16 }}>
          <div style={{ display: "grid", gridTemplateColumns: `repeat(${Math.max(1, months.length)}, 1fr)`,
            gap: 6, alignItems: "end", height: 180 }}>
            {months.map(m => {
              const h = Math.max(8, (m.total / maxTotal) * 150);
              const color = (m.delta_pct == null) ? "#94a3b8"
                : m.delta_pct >= 0 ? "#22c55e" : "#ef4444";
              return (
                <div key={m.month} style={{ textAlign: "center" }} data-testid={`mom-${m.month}`}>
                  <div title={`${fmt(m.total)} (${m.count} sales)`} style={{ background: color, height: h, borderRadius: 3, marginBottom: 4, transition: "height 0.3s" }} />
                  <div style={{ fontSize: 9, color: "var(--text3)" }}>{m.month.slice(5)}</div>
                  <div style={{ fontSize: 10, fontWeight: 600 }}>{fmt(m.total)}</div>
                  {m.delta_pct != null && (
                    <div style={{ fontSize: 9, color: m.delta_pct >= 0 ? "#22c55e" : "#ef4444" }}>
                      {m.delta_pct >= 0 ? "▲" : "▼"} {Math.abs(m.delta_pct)}%
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <div className="card" data-testid="sales-quarterly-card" style={{ marginBottom: 16 }}>
        <div className="card-header">
          <span className="card-title">Quarterly Goals · Year</span>
          <select value={year} onChange={e => setYear(parseInt(e.target.value))}
            data-testid="quarterly-year"
            style={{ marginLeft: 10, fontSize: 11, padding: "3px 8px" }}>
            {[new Date().getFullYear() - 1, new Date().getFullYear(), new Date().getFullYear() + 1].map(y => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>
        </div>
        <div style={{ padding: 16, display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(220px,1fr))", gap: 12 }}>
          {(qData?.quarters || []).map(q => {
            const status = q.progress_pct == null ? "gray"
              : q.progress_pct >= 100 ? "green"
              : q.progress_pct >= 70 ? "yellow" : "red";
            const sBg = status === "green" ? "#dcfce7" : status === "yellow" ? "#fef3c7" : status === "red" ? "#fee2e2" : "#f1f5f9";
            const sFg = status === "green" ? "#166534" : status === "yellow" ? "#92400e" : status === "red" ? "#991b1b" : "#475569";
            return (
              <div key={q.period} data-testid={`quarter-${q.period}`}
                style={{ background: sBg, color: sFg, padding: 12, borderRadius: 8 }}>
                <div style={{ fontSize: 11, letterSpacing: 1, opacity: 0.8 }}>{q.period}</div>
                <div style={{ fontSize: 22, fontWeight: 700, fontFamily: "var(--rajdhani)" }}>{fmt(q.total)}</div>
                <div style={{ fontSize: 11, opacity: 0.8 }}>{q.count} sales</div>
                <div style={{ fontSize: 11, marginTop: 6 }}>
                  Goal: <strong>{fmt(q.goal_total)}</strong>
                  {q.progress_pct != null && <span style={{ marginLeft: 6 }}>· {q.progress_pct}%</span>}
                </div>
                <button onClick={() => { setEditQ(q.period); setTarget(""); }}
                  data-testid={`quarter-set-${q.period}`}
                  className="btn btn-ghost" style={{ fontSize: 10, marginTop: 8, padding: "3px 8px" }}>
                  Set / Add goal
                </button>
              </div>
            );
          })}
        </div>

        {editQ && (
          <div className="modal-overlay" onClick={() => setEditQ(null)}>
            <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 420 }} data-testid="quarter-edit-modal">
              <div className="modal-header">
                <div style={{ fontFamily: "var(--rajdhani)", fontSize: 16, fontWeight: 700 }}>Quarterly Goal · {editQ}</div>
                <button className="icon-btn" onClick={() => setEditQ(null)}>×</button>
              </div>
              <div style={{ padding: 18 }}>
                <div className="form-group">
                  <div className="form-lbl">Location</div>
                  <select value={wsSel} onChange={e => setWsSel(e.target.value)} className="form-control" style={{ width: "100%" }} data-testid="quarter-edit-ws">
                    {(locations || []).map(l => <option key={l.id} value={String(l.id)}>{l.name}</option>)}
                  </select>
                </div>
                <div className="form-group" style={{ marginTop: 12 }}>
                  <div className="form-lbl">Target ($)</div>
                  <input type="number" min="0" step="100" value={target} onChange={e => setTarget(e.target.value)}
                    className="form-control" style={{ width: "100%" }} data-testid="quarter-edit-target" />
                </div>
                <div style={{ display: "flex", gap: 8, marginTop: 16 }}>
                  <button onClick={saveQuarterGoal} className="btn btn-primary" data-testid="quarter-edit-save">Save Goal</button>
                  <button onClick={() => setEditQ(null)} className="btn btn-ghost">Cancel</button>
                </div>
                <div style={{ fontSize: 10, color: "var(--text3)", marginTop: 10 }}>
                  Quarterly goals roll up across all locations into the dashboard above.
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
}


// ─── 4) Link Team Member picker (used by PayrollPage Add modal) ────────────
export function LinkTeamMemberPicker({ onPick, valueId }) {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    j("/api/iv/team").then(r => setMembers(r.team || r.members || [])).catch(() => {}).finally(() => setLoading(false));
  }, []);

  if (loading) return <div style={{ fontSize: 11, color: "var(--text3)" }}>Loading Apex team roster…</div>;
  if (!members.length) return (
    <div style={{ fontSize: 11, color: "var(--text3)" }}>
      No Apex team members found. Add team members in <strong>Administration → Team and Access</strong> first.
    </div>
  );

  return (
    <select
      data-testid="link-team-member-select"
      className="form-control"
      style={{ width: "100%" }}
      value={valueId || ""}
      onChange={e => {
        const id = e.target.value;
        if (!id) { onPick(null); return; }
        const m = members.find(x => x.id === id);
        if (m) onPick({
          id: m.id,
          email: m.email,
          name: m.full_name || `${m.first_name || ""} ${m.last_name || ""}`.trim() || m.email,
          role: m.system_role || m.role,
          phone: m.phone,
          job_title: m.system_role || m.role || "",
        });
      }}>
      <option value="">— Optional · Link to Apex team member —</option>
      {members.map(m => {
        const display = m.full_name || `${m.first_name || ""} ${m.last_name || ""}`.trim() || m.email;
        return (
          <option key={m.id} value={m.id}>
            {display} · {m.email} {m.system_role ? `· ${m.system_role}` : ""}
          </option>
        );
      })}
    </select>
  );
}
